import { Navigate } from "react-router-dom";
import { useAuth } from "./AuthProvider.jsx";
import { NameSetup } from "./NameSetup.jsx";
import { SevricLoader } from "../../../chat/src/index.js";

// Route guard. Renders children only if the user is authenticated AND has
// set their display name. While auth resolves, shows the SEVRIC boot
// spinner. First-time users (no displayName) are intercepted by NameSetup
// before they hit any real page.
//
// Status states:
//   loading        — auth not yet resolved, show spinner
//   authenticated  — render children (or NameSetup if displayName missing)
//   unauthenticated— never had a token / sign-out, bounce to portal/signin
//   error          — SSO bridge failed; render the error on-page so the user
//                    can see what broke. NEVER silently bounce on error —
//                    that strands the user with no way to debug.
//
// Usage: <Route element={<RequireAuth><Layout/></RequireAuth>}> ... </Route>
export function RequireAuth({ children }) {
  const { status, profile, error } = useAuth();
  if (status === "loading") {
    return <SevricLoader app="battery care" />;
  }
  if (status === "error") {
    // Show a visible error screen instead of bouncing. The user can see what
    // failed and either re-launch from the portal or sign out.
    return <SsoErrorScreen error={error} />;
  }
  if (status !== "authenticated") {
    // Bounce to the portal's signin page — apps don't host their own login UI.
    window.location.replace("https://portal.sevric.com/signin");
    return null;
  }
  // First-login gate. Until the user picks a name, they can't reach any
  // real page — their name shows up on every reading they record, so it has
  // to exist before they can act.
  if (!profile?.displayName) {
    return <NameSetup />;
  }
  return children;
}

function SsoErrorScreen({ error }) {
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  const hadHash = hash.includes("portalToken=");
  return (
    <main className="container" style={{ paddingTop: 80 }}>
      <div className="panel" style={{
        maxWidth: 600, margin: "0 auto",
        borderColor: "rgba(124,41,53,0.30)",
      }}>
        <div className="eyebrow" style={{ color: "var(--danger)" }}>SSO bridge failed</div>
        <h1 style={{ marginTop: 10, fontSize: 22, color: "var(--text)" }}>
          Couldn't complete sign-in to Battery Care.
        </h1>
        <p style={{ marginTop: 12, fontSize: 13.5 }}>
          The portal minted a token, but this app couldn't consume it.
          Diagnostics:
        </p>
        <div style={{
          marginTop: 12, padding: "12px 14px",
          background: "rgba(255,255,255,0.04)", borderRadius: 10,
          fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace",
          fontSize: 12, color: "var(--text-muted)",
          whiteSpace: "pre-wrap", wordBreak: "break-word",
        }}>
{`error:        ${error || "(no error message)"}
hash present: ${hadHash ? "yes" : "no"}
url path:     ${typeof window !== "undefined" ? window.location.pathname : "?"}
referrer:     ${typeof document !== "undefined" ? document.referrer || "(none)" : "?"}`}
        </div>
        <p style={{ marginTop: 14, fontSize: 12.5, color: "var(--text-dim)" }}>
          {hadHash
            ? "The portal token reached this app but the SEVRIC session couldn't be established. Most common causes: (a) the access token expired (~1h validity); (b) this app isn't authorized for your account yet; (c) network blocked by CSP."
            : "No portal token in the URL. You probably navigated here directly. Re-launch from the portal: portal.sevric.com → Battery Care tile."}
        </p>
        <div style={{ marginTop: 18, display: "flex", gap: 10, flexWrap: "wrap" }}>
          <a href="https://portal.sevric.com" className="btn primary">← SEVRIC portal</a>
          <a href="https://portal.sevric.com/launch.html?app=battery" className="btn">Try launching again</a>
        </div>
      </div>
    </main>
  );
}
